import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ArrowLeft, X, ClipboardList, Stethoscope, MessageSquare, FileCheck } from 'lucide-react';
import ReelListenButton from '../components/ReelListenButton';
import '../styles/Demo.css';

/**
 * /demo — the public "See How It Works" walkthrough.
 *
 * Structure mirrors /our-story so the demo feels like an extension of the
 * brand voice rather than a one-off marketing page:
 *   - 5W's-style debossed foil plaque ("Step inside CADENCE")
 *   - Four 4:5 playbill posters, one per stage of the patient journey
 *   - Tapping a poster opens the parchment cinematic scene
 *   - Closing CTA links into a real, no-sign-up-required intake preview.
 *
 * AI-prescribing rule: nothing in the demo claims diagnostic / prescribing
 * action — only review, translation, advocacy.
 */
const demoStages = [
  {
    id: 'arrive',
    icon: ClipboardList,
    label: 'Stage 01 \u2014 You Bring the Story',
    highlight: 'Whatever you have. Whatever shape it\u2019s in.',
    paragraphs: [
      "Three folders. A screenshot of last year's labs. A Notes-app dump of symptoms in your own words. A specialist's letter you can't decipher.",
      "Walk in with whatever you have. We meet you exactly where you are\u2014no PDF formatting required, no medical vocabulary expected. The work of organizing the records is ours, not yours.",
    ],
  },
  {
    id: 'read',
    icon: Stethoscope,
    label: 'Stage 02 \u2014 We Read Every Page',
    highlight: 'Not skim. Read.',
    paragraphs: [
      "Our clinical experts\u201436+ years of combined military medical experience\u2014read every record you send. Labs. Imaging reports. Op notes. Consult letters. The follow-up that never happened.",
      "Patterns hide in the spaces between specialists. We find them: the lab value that drifted three years before anyone flagged it. The medication that was tapered without explanation. The diagnosis that was floated, then quietly dropped from the chart.",
    ],
  },
  {
    id: 'translate',
    icon: MessageSquare,
    label: 'Stage 03 \u2014 We Translate',
    highlight: 'Plain language. Yours.',
    paragraphs: [
      "We render the entire story in language you can actually read. What was found. What was missed. What it might mean. Where the gaps are.",
      "No diagnoses, no prescriptions, no treatment plans\u2014that\u2019s your physician\u2019s lane. What we give you is the full picture so the conversation with your physician can finally be a conversation.",
    ],
  },
  {
    id: 'plan',
    icon: FileCheck,
    label: 'Stage 04 \u2014 You Walk Out With a Plan',
    highlight: 'A doctor-ready script. In your hands.',
    paragraphs: [
      "You leave with a written advocacy script: the exact questions to ask, the tests worth requesting, the records worth surfacing, the language that gets a busy clinician to lean in.",
      "Your next appointment isn\u2019t another guessing game. It\u2019s a conversation you walked in prepared for\u2014because you finally have the full story in your hands.",
    ],
  },
];

const audienceTracks = [
  {
    id: 'patient',
    label: 'For Patients',
    body: "If you've been told everything is normal but you don't feel normal, this is for you. We're the second set of eyes\u2014trained in military precision, paid by you, accountable to you.",
  },
  {
    id: 'provider',
    label: 'For Providers',
    body: "Send us the complex patient who needs more time than a 15-minute slot allows. We do the deep-dive record review, return a written synthesis, and your patient comes back to you better prepared and better organized.",
  },
  {
    id: 'company',
    label: 'For Companies',
    body: "Employers, benefits teams, and concierge programs partner with CADENCE for navigation services that reduce member burnout and surface the questions a 15-minute appointment will never ask. Group engagements available.",
  },
];

const Demo = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [sceneKey, setSceneKey] = useState(0);
  const [activeTrack, setActiveTrack] = useState('patient');
  // Hands-free narration session (see Our Story for the pattern).
  const [narrating, setNarrating] = useState(false);
  const [autoplay, setAutoplay] = useState(() => {
    try {
      return localStorage.getItem('cadence_reel_autoplay') !== 'false';
    } catch {
      return true;
    }
  });
  const navigate = useNavigate();

  const toggleAutoplay = (val) => {
    setAutoplay(val);
    try {
      localStorage.setItem('cadence_reel_autoplay', val ? 'true' : 'false');
    } catch {
      /* ignore */
    }
  };

  // Flatten a stage into a clean narration string (descriptor + highlight
  // + paragraphs) with smart quotes / nbsp stripped for natural TTS.
  const stageNarration = (stage) => {
    if (!stage) return '';
    const parts = [];
    const descriptor = stage.label.includes(' \u2014 ')
      ? stage.label.split(' \u2014 ')[1]
      : stage.label;
    if (descriptor) parts.push(`${descriptor}.`);
    if (stage.highlight) parts.push(stage.highlight);
    if (Array.isArray(stage.paragraphs)) parts.push(...stage.paragraphs);
    return parts
      .join(' ')
      .replace(/[\u201C\u201D]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };
  const location = useLocation();
  // Context-aware back nav: return the visitor to wherever they entered
  // from (e.g. CADENCE Promise or the Home/Landing page) instead of always
  // bouncing to the landing. Falls back to browser-history, then /coming-soon
  // for deep links (user feedback 2026-06: "it just bounces back to page one").
  const backLabel = location.state?.fromLabel
    ? `Back to ${location.state.fromLabel}`
    : 'Back';
  const handleBack = (e) => {
    e.preventDefault();
    if (location.state?.from) navigate(location.state.from);
    else if (location.key !== 'default') navigate(-1);
    else navigate('/coming-soon');
  };

  const openStage = (idx) => {
    setNarrating(false);
    setActiveIndex(idx);
    setSceneKey((k) => k + 1);
  };
  const closeReel = () => {
    setNarrating(false);
    setActiveIndex(null);
    // Return to the TOP of the page on close so the visitor lands back on
    // the How-It-Works intro instead of being dumped deep at the four stage
    // playbills (user feedback 2026-06: "it clips to the bottom of the four
    // stages — you've got to roll up to get back to the top").
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };
  // Reset the cinematic overlay's internal scroll to the top whenever a
  // new stage opens — otherwise the visitor lands mid-page and feels
  // like they "snapped" to the middle (user feedback 2026-02). We scroll
  // the overlay AND its inner scene wrapper because either can be the
  // scrollable element depending on viewport.
  const scrollOverlayToTop = () => {
    requestAnimationFrame(() => {
      ['.story-reel-overlay', '.story-reel-scene', '.story-reel-body', '.story-reel-content']
        .forEach((sel) => {
          document.querySelectorAll(sel).forEach((el) => { el.scrollTop = 0; });
        });
    });
  };
  const nextStage = () => {
    if (activeIndex === null) return;
    setNarrating(false);
    if (activeIndex >= demoStages.length - 1) {
      setActiveIndex(null);
    } else {
      setActiveIndex(activeIndex + 1);
      setSceneKey((k) => k + 1);
      scrollOverlayToTop();
    }
  };
  const prevStage = () => {
    if (activeIndex === null || activeIndex === 0) return;
    setNarrating(false);
    setActiveIndex(activeIndex - 1);
    setSceneKey((k) => k + 1);
    scrollOverlayToTop();
  };

  // Autoplay: advance to the next stage (which auto-plays) when narration
  // of the current stage ends.
  const handleStageEnded = () => {
    if (autoplay && activeIndex !== null && activeIndex < demoStages.length - 1) {
      setActiveIndex(activeIndex + 1);
      setSceneKey((k) => k + 1);
      scrollOverlayToTop();
    } else {
      setNarrating(false);
    }
  };

  // Keyboard + scroll-lock — matches Our Story reel UX exactly.
  useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeReel();
      else if (e.key === 'ArrowRight') nextStage();
      else if (e.key === 'ArrowLeft') prevStage();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  useEffect(() => {
    if (activeIndex !== null) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [activeIndex]);

  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) navigate('/home');
  };

  const activeStage = activeIndex !== null ? demoStages[activeIndex] : null;
  const activeTrackData = audienceTracks.find((t) => t.id === activeTrack);

  return (
    <div
      className="demo-page"
      data-testid="demo-page"
      onClick={handleBackgroundClick}
      role="presentation"
    >
      <Helmet>
        <title>See How It Works | CADENCE Medical Consulting</title>
        <meta name="description" content="A guided tour of the CADENCE experience\u2014from the moment you bring us your records to the day you walk into your next appointment with a written advocacy script in hand." />
        <link rel="canonical" href="https://www.cadencemedicalconsulting.com/demo" />
      </Helmet>

      {/* Emblem */}
      <section className="emblem-section">
        <div className="container">
          <div className="emblem-wrapper">
            <img
              src="/cadence-gold-foil.png"
              alt="CADENCE Medical Consulting"
              className="velvet-emblem-home"
            />
          </div>
        </div>
      </section>

      {/* Title section REMOVED 2026-02 (founder feedback: "the title how
          it works does not need to be repeated plus it's too big"). The
          plaque below already carries the same messaging — eyebrow
          "Step inside" + title "The Journey" + subline "from records in
          hand · to advocacy in writing" — so the giant H1 was redundant
          and competing for the visitor's eye. Keeping only the plaque. */}

      {/* Foil-Debossed Plaque — same chassis as the 5W's. Click the
          plaque to auto-open Stage 1 (founder feedback 2026-02
          "make it so you can click on the title to automatically
          start at stage 1"). */}
      <section className="demo-plaque-section">
        <div className="container">
          <div className="story-playbills-intro-wrap">
            <button
              type="button"
              className="story-fivew-plaque story-fivew-plaque-clickable"
              onClick={() => openStage(0)}
              aria-label="Begin the journey from Stage 01"
              data-testid="demo-plaque-begin-journey"
            >
              <div className="story-fivew-plaque-inner">
                <span className="story-fivew-eyebrow">Step inside</span>
                <h2 className="story-fivew-title">The Journey</h2>
                <span className="story-fivew-footnote">from records in hand &middot; to advocacy in writing</span>
                <span className="story-fivew-plaque-hint">Walk through it with us &rarr;</span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Audience switcher — three tracks on a single foil tile.
          Placed BEFORE the stage playbills so visitors can self-select
          their lens (patient / provider / company) before reading the
          journey below. Per user feedback 2026-02. */}
      <section className="demo-audience-section" data-testid="demo-audience">
        <div className="container">
          <div className="demo-audience-tabs" role="tablist" aria-label="Who this is for">
            {audienceTracks.map((track) => (
              <button
                key={track.id}
                type="button"
                role="tab"
                aria-selected={activeTrack === track.id}
                className={`demo-audience-tab ${activeTrack === track.id ? 'is-active' : ''}`}
                onClick={(e) => {
                  // Capture scroll before the panel re-renders so the
                  // browser can't focus-scroll us to the footer. The
                  // panel below changes height when the track changes;
                  // restoring scroll keeps the visitor's place.
                  const y = window.scrollY;
                  setActiveTrack(track.id);
                  e.currentTarget.blur();
                  requestAnimationFrame(() => window.scrollTo({ top: y }));
                }}
                data-testid={`demo-audience-tab-${track.id}`}
              >
                {track.label}
              </button>
            ))}
          </div>
          <div className="demo-audience-panel" data-testid="demo-audience-panel">
            <p>{activeTrackData.body}</p>
          </div>
        </div>
      </section>

      {/* Four stage playbills */}
      <section className="story-playbills-section" data-testid="demo-stages">
        <div className="container">
          <div className="story-playbills">
            {demoStages.map((stage, idx) => {
              const [prefix, descriptorRaw] = stage.label.includes(' \u2014 ')
                ? stage.label.split(' \u2014 ')
                : [stage.label, null];
              return (
                <button
                  key={stage.id}
                  className="story-playbill"
                  onClick={() => openStage(idx)}
                  data-testid={`demo-stage-${stage.id}`}
                  aria-label={`Open stage: ${stage.label}`}
                >
                  <span className="story-playbill-num">Stage {String(idx + 1).padStart(2, '0')}</span>
                  <span className="story-playbill-rule" aria-hidden="true" />
                  <span className="story-playbill-label">
                    <span className="story-playbill-label-prefix">{prefix}</span>
                    {descriptorRaw && (
                      <span className="story-playbill-label-descriptor">{descriptorRaw}</span>
                    )}
                  </span>
                  <span className="story-playbill-cta">Open Stage <ArrowRight size={14} /></span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Closing copper tile — uses the canonical site-wide pattern */}
      <section className="clarity-tile-section" data-testid="demo-closing-tile">
        <div className="container">
          <div className="clarity-copper-tile">
            <p className="clarity-tile-text">
              Ready to be heard? Begin your intake &mdash; no account required.
            </p>
          </div>
        </div>
      </section>

      {/* Twin CTAs — try the intake (low commit) + schedule a call (higher commit) */}
      <section className="demo-cta-section">
        <div className="container">
          <div className="demo-cta-row">
            <Link
              to="/portal?guest=true&tab=intake"
              className="demo-cta demo-cta-primary"
              data-testid="demo-cta-try-intake"
            >
              Begin the Intake <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              className="demo-cta demo-cta-secondary"
              data-testid="demo-cta-schedule"
            >
              Schedule a Call <ArrowRight size={16} />
            </Link>
          </div>
          <p className="demo-cta-aside">
            The intake auto-saves on your device. You can walk away mid-question and come back later &mdash; no account required to start.
          </p>
        </div>
      </section>

      {/* Back nav — context-aware: returns the visitor to their entry
          point (CADENCE Promise / Home) via location.state, falling back to
          browser history, then /coming-soon for deep links. Replaces the old
          hardcoded "Back to Landing" that bounced everyone to page one
          (user feedback 2026-06). */}
      <section className="demo-back-section">
        <a href="#" onClick={handleBack} className="demo-back-link" data-testid="demo-back-landing">
          <ArrowLeft size={16} />
          {backLabel}
        </a>
      </section>

      {/* Footer rendered globally by App.js — no per-page <Footer/> here */}

      {/* Cinematic scene overlay — parchment backdrop, same chassis as Our Story */}
      {activeStage && (
        <div
          className="story-reel-overlay"
          data-testid="demo-stage-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Stage: ${activeStage.label}`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="story-reel-close"
            onClick={closeReel}
            aria-label="Close stage"
            data-testid="demo-stage-close"
          >
            <X size={22} />
          </button>

          <div className="story-reel-header">
            <span className="story-reel-chapter-num">
              Stage {String(activeIndex + 1).padStart(2, '0')}
              <span className="story-reel-chapter-of"> of {String(demoStages.length).padStart(2, '0')}</span>
            </span>
            <div className="story-reel-dots" aria-hidden="true">
              {demoStages.map((s, i) => (
                <span
                  key={s.id}
                  className={`story-reel-dot ${i === activeIndex ? 'is-active' : ''} ${i < activeIndex ? 'is-past' : ''}`}
                />
              ))}
            </div>
          </div>

          <div className="story-reel-scene" key={sceneKey} data-testid="demo-stage-scene">
            <h2 className="story-reel-label">
              <span className="story-reel-label-ornament" aria-hidden="true" />
              {activeStage.label}
              <span className="story-reel-label-ornament" aria-hidden="true" />
            </h2>

            {activeStage.highlight && (
              <blockquote className="story-reel-highlight">
                {activeStage.highlight}
              </blockquote>
            )}

            <ReelListenButton
              key={`demo-listen-${activeIndex}`}
              text={stageNarration(activeStage)}
              autoStart={narrating}
              onStart={() => setNarrating(true)}
              onEnded={handleStageEnded}
              autoplayEnabled={autoplay}
              onToggleAutoplay={toggleAutoplay}
              label="Listen to this stage"
              autoplayLabel="Auto-play stages"
              testId="demo-stage-listen"
            />

            <div className="story-reel-narrative">
              {activeStage.paragraphs.map((p, idx) => (
                <p
                  key={idx}
                  className="story-reel-paragraph"
                  style={{ animationDelay: `${0.7 + idx * 0.25}s` }}
                >
                  {p}
                </p>
              ))}
            </div>
          </div>

          {/* Floating side-edge arrows — sticky to viewport edges so
              the visitor can navigate stages WITHOUT scrolling to the
              bottom of long stage content (user feedback 2026-02). */}
          <button
            type="button"
            className="story-reel-side-arrow story-reel-side-arrow--prev"
            onClick={prevStage}
            disabled={activeIndex === 0}
            aria-label="Previous stage"
            data-testid="demo-stage-side-prev"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            className="story-reel-side-arrow story-reel-side-arrow--next"
            onClick={nextStage}
            aria-label={activeIndex === demoStages.length - 1 ? 'Close reel' : 'Next stage'}
            data-testid="demo-stage-side-next"
          >
            <ArrowRight size={20} strokeWidth={2.5} />
          </button>

          <div className="story-reel-nav">
            <button
              type="button"
              className="story-reel-nav-btn prev"
              onClick={prevStage}
              disabled={activeIndex === 0}
              data-testid="demo-stage-prev"
              aria-label="Previous stage"
            >
              <ArrowLeft size={16} />
              <span>Previous</span>
            </button>

            <button
              type="button"
              className="story-reel-nav-btn next"
              onClick={nextStage}
              data-testid="demo-stage-next"
              aria-label={activeIndex === demoStages.length - 1 ? 'Close tour' : 'Next stage'}
            >
              <span>
                {activeIndex === demoStages.length - 1 ? 'Close Tour' : 'Continue'}
              </span>
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Demo;
